import React, { Component } from "react";
import { Link } from "react-router";
import PermissionLevel from "../../data/PermissionLevel";
import Menu from "../Menu";
import SiteNav from "./SiteNav";
import "./Header.css";
import PropTypes from "prop-types";
import logoSrc from "../../assets/images/clear-logo.svg";
import axios from "axios";
import { io } from "socket.io-client";
import { APPLICATION_STATUS_ID_SUBMITTED, APPLICATION_STATUS_ID_ACCEPTED } from '../Application/constants';
import UserAvatar from '../UserAvatar/UserAvatar';
import { RATING_WINDOW_EVENT, nextRatingBoundaryDelay } from '../../utils/ratingWindow';

const siteLinks = [
  {
    name: "About Us",
    path: "/about",
  },
  {
    name: "Projects",
    path: "/projects",
    loginRequired: true,
  },
  {
    name: "Apply",
    path: "/apply",
    loginRequired: true,
  },
];

const userLinks = [
  {
    name: "Admin Settings",
    path: "/admin-settings",
    permission_level: PermissionLevel.Officer,
  },
  {
    name: "Application Rater",
    path: "/application-rater",
    permission_level: PermissionLevel.Committee,
    requiresTcmPermission: true,
    requiresRatingOpen: true,
  },
  {
    name: "Dashboard",
    path: "/dashboard",
    permission_level: PermissionLevel.Mentee,
  },
  {
    name: "Manager Approval",
    path: "/manager-approval",
    permission_level: PermissionLevel.User,
    requiresManagerApprovals: true,
  },
  {
    name: "Graduation Pledge",
    path: "/graduation-pledge",
    permission_level: PermissionLevel.User,
    requiresGradPledge: true,
  },
  {
    name: "Relationships",
    path: "/relationships",
    permission_level: PermissionLevel.Mentor,
    requiresSubmittedApplication: true,
  },
];

class Header extends Component {
  state = {
    hasSubmittedApplication: false,
    applicationChecked: false,
    hasManagerApprovals: false,
    managerApprovalsPendingCount: 0,
    managerApprovalsChecked: false,
    tcmCanView: true,
    ratingOpen: true,
    tcmPermissionChecked: false,
    darkModeMinLevel: 0, // minimum permission_level required to use dark mode
    projectFairNavYear: 2025,
    hasGradPledge: false,
    gradPledgeChecked: false,
  };

  componentDidMount = () => {
    // Check for submitted application when component mounts
    this.checkForSubmittedApplication();
    this.checkForManagerApprovals();
    this.checkTcmPermissions();
    this.checkSiteSettings();
    this.checkRatingWindow();
    this.checkForGradPledge();

    // Listen for application updates
    window.addEventListener('applicationUpdated', this.handleApplicationUpdate);
    window.addEventListener('siteSettingsUpdated', this.handleSiteSettingsUpdate);
    window.addEventListener(RATING_WINDOW_EVENT, this.handleRatingWindowUpdate);

    // Socket subscription for real-time manager approval badge updates
    this._socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });
    this._socket.on('manager-approval:updated', () => this.checkForManagerApprovals());
    this._socket.on('manager-approval-config:updated', () => this.checkForManagerApprovals());
    this._socket.on('rating-window:updated', this.handleRatingWindowUpdate);
  };

  componentWillUnmount = () => {
    window.removeEventListener('applicationUpdated', this.handleApplicationUpdate);
    window.removeEventListener('siteSettingsUpdated', this.handleSiteSettingsUpdate);
    window.removeEventListener(RATING_WINDOW_EVENT, this.handleRatingWindowUpdate);
    clearTimeout(this._ratingTimer);
    if (this._socket) {
      this._socket.off('manager-approval:updated');
      this._socket.off('manager-approval-config:updated');
      this._socket.off('rating-window:updated');
      this._socket.disconnect();
    }
  };

  handleApplicationUpdate = () => {
    console.log('=== APPLICATION UPDATE EVENT RECEIVED ===');
    
    this.checkForSubmittedApplication();
    this.checkForManagerApprovals();
  };

  // Add method to check for submitted application
  checkForSubmittedApplication = async () => {
    if (!this.props.currentUser?.user_id) {
      this.setState({ applicationChecked: true });
      return;
    }

    try {
      const response = await axios.get('/api/applications/my_application');
      const currentYear = new Date().getFullYear();
      
      const hasSubmittedApp = response.data.some((app) => {
        //const isActive = app.active === true;
        const isSubmitted = app.status_id === APPLICATION_STATUS_ID_SUBMITTED;
        const isApproved = app.status_id === APPLICATION_STATUS_ID_ACCEPTED;
        const isCurrentYear = app.year === currentYear;
        const isUser = app.user_id === this.props.currentUser.user_id;
        return (isSubmitted || isApproved) && isCurrentYear && isUser;
      });

      console.log('=== CHECK FOR SUBMITTED APPLICATION ===');
      console.log('Submitted Application:', hasSubmittedApp);
      this.setState({
        hasSubmittedApplication: hasSubmittedApp,
        applicationChecked: true 
      });
    } catch (error) {
      console.error('Error checking for submitted application:', error);
      this.setState({ applicationChecked: true });
    }
  };

  // Check for manager approvals
  checkForManagerApprovals = async () => {
    if (!this.props.currentUser?.user_id) {
      this.setState({ managerApprovalsChecked: true });
      return;
    }

    try {
      const [response, configRes] = await Promise.all([
        axios.get('/api/applications/my_application'),
        axios.get('/api/manager_approval_type_config'),
      ]);
      const currentYear = new Date().getFullYear();
      const typeConfigs = configRes.data || [];

      const isRequiredType = (app) => {
        const chapterConfig = typeConfigs.find(c => c.chapter_id === app.chapter_id);
        const config = chapterConfig || { mentee_required: true, mentor_required: false, tcm_required: false };
        const appType = (app.ApplicantType?.applicant_type || '').toLowerCase();
        if (appType === 'mentee') return config.mentee_required;
        if (appType === 'mentor') return config.mentor_required;
        if (appType === 'commitee' || appType === 'committee') return config.tcm_required;
        return true;
      };
      
      // Show menu if user has ANY manager relationship in the current year (not just submitted)
      // filtered by type config
      const managerApps = response.data.filter((app) => {
        const isCurrentYear = app.year === currentYear;
        const isNotApplicant = app.user_id !== this.props.currentUser.user_id;
        return isCurrentYear && isNotApplicant && isRequiredType(app);
      });

      const hasManagerApprovals = managerApps.length > 0;

      // Pending count: submitted apps where no manager approval record or approved is null
      const pendingCount = managerApps.filter((app) => {
        const isSubmitted = app.status_id === 2 && !!app.submitted_at;
        return isSubmitted && (!app.ManagerApproval || app.ManagerApproval.approved === null || app.ManagerApproval.approved === undefined);
      }).length;

      this.setState({
        hasManagerApprovals,
        managerApprovalsPendingCount: pendingCount,
        managerApprovalsChecked: true 
      });
    } catch (error) {
      console.error('Error checking for manager approvals:', error);
      this.setState({ managerApprovalsChecked: true });
    }
  };

  checkForGradPledge = async () => {
    if (!this.props.currentUser?.user_id) {
      this.setState({ gradPledgeChecked: true });
      return;
    }
    try {
      const response = await axios.get('/api/grad_pledges/my_participation');
      this.setState({
        hasGradPledge: response.data.hasParticipation,
        gradPledgeChecked: true,
      });
    } catch {
      this.setState({ gradPledgeChecked: true });
    }
  };

  // Hide the Rater link while the chapter's rating window is closed. Re-checked live on
  // rating-window socket/window events, and on a timer at the next open/close moment.
  checkRatingWindow = async () => {
    const { currentUser } = this.props;
    clearTimeout(this._ratingTimer);
    if (!currentUser?.chapter_id || currentUser.Role?.permission_level < PermissionLevel.Committee) return;
    try {
      const { data } = await axios.get(`/api/rating_windows/status/${currentUser.chapter_id}`);
      this.setState({ ratingOpen: data.open !== false });
      const delay = nextRatingBoundaryDelay(data);
      if (delay !== null) this._ratingTimer = setTimeout(this.checkRatingWindow, delay);
    } catch {
      // Fail open: the server still enforces the window.
    }
  };

  handleRatingWindowUpdate = (payload) => {
    const chapterId = payload?.chapter_id ?? payload?.detail?.chapter_id;
    if (chapterId === undefined || Number(chapterId) === Number(this.props.currentUser?.chapter_id)) {
      this.checkRatingWindow();
    }
  };

  // Check TCM application permissions (only relevant for committee-level users)
  checkTcmPermissions = async () => {
    if (this.props.currentUser?.Role?.permission_level !== PermissionLevel.Committee) {
      this.setState({ tcmPermissionChecked: true });
      return;
    }
    try {
      const perms = await axios.get('/api/chapter_application_permissions/all').then(r => r.data);
      const myPerm = perms.find(p => p.chapter_id === this.props.currentUser.chapter_id);
      this.setState({
        tcmCanView: !myPerm || myPerm.tcm_can_view,
        tcmPermissionChecked: true,
      });
    } catch {
      // On error, default to showing the links (server will enforce access)
      this.setState({ tcmPermissionChecked: true });
    }
  };

  checkSiteSettings = async () => {
    try {
      const settings = await axios.get('/api/adminSettings/settings').then(r => r.data);
      const darkModeSetting = settings.find(s => s.setting_key === 'dark_mode_min_level');
      const projectFairYearSetting = settings.find(s => s.setting_key === 'project_fair_nav_year');
      this.setState({
        darkModeMinLevel: darkModeSetting ? (parseInt(darkModeSetting.setting_value) || 0) : 0,
        projectFairNavYear: projectFairYearSetting
          ? (parseInt(projectFairYearSetting.setting_value) || 2025)
          : 2025,
      });
    } catch {
      // Preserve safe defaults when global settings cannot be loaded.
    }
  };

  handleSiteSettingsUpdate = (event) => {
    const year = parseInt(event.detail?.projectFairNavYear, 10);
    if (Number.isInteger(year)) {
      this.setState({ projectFairNavYear: year });
    }
  };

  // Update componentDidUpdate to check for application when user changes
  componentDidUpdate = (prevProps) => {
    if (prevProps.currentUser?.user_id !== this.props.currentUser?.user_id) {
      this.checkForSubmittedApplication();
      this.checkForManagerApprovals();
      this.checkTcmPermissions();
      this.checkSiteSettings();
      this.checkForGradPledge();
    }
    if (
      prevProps.currentUser?.user_id !== this.props.currentUser?.user_id ||
      prevProps.currentUser?.chapter_id !== this.props.currentUser?.chapter_id
    ) {
      this.checkRatingWindow();
    }
  };

  filterLinks = (link) => {
    const hasPermission = (this.props.currentUser?.Role.permission_level || 0) >= link.permission_level;

    // If link requires submitted application, check for it
    if (link.requiresSubmittedApplication && this.state.applicationChecked) {
      return hasPermission && this.state.hasSubmittedApplication;
    }

    // If link requires manager approvals, check for them
    if (link.requiresManagerApprovals && this.state.managerApprovalsChecked) {
      return hasPermission && this.state.hasManagerApprovals;
    }

    // If link requires grad pledge participation, hide it for non-participants
    if (link.requiresGradPledge && this.state.gradPledgeChecked) {
      return hasPermission && this.state.hasGradPledge;
    }

    // Hide the Rater from TCMs whose chapter has access disabled
    if (link.requiresRatingOpen && !this.state.ratingOpen) {
      return false;
    }

    if (link.requiresTcmPermission && this.props.currentUser?.Role?.permission_level === PermissionLevel.Committee) {
      return hasPermission && this.state.tcmCanView;
    }

    return hasPermission;
  };

  render = () => {
    const projectFairLink = {
      name: `Project Fairs ${this.state.projectFairNavYear}`,
      path: `/project-fair/${this.state.projectFairNavYear}`,
      loginRequired: true,
    };
    const filteredSiteLinks = [...siteLinks, projectFairLink].filter(l=>!l.loginRequired || !!this.props.currentUser)
    const filteredUserLinks = userLinks.filter(this.filterLinks).map(link => {
      // Add badge to Manager Approval link — yellow if pending, green if all caught up
      if (link.requiresManagerApprovals && this.state.hasManagerApprovals) {
        const pending = this.state.managerApprovalsPendingCount;
        const badgeModifier = pending > 0 ? 'menu-badge--pending' : 'menu-badge--clear';
        const badgeText = pending > 0 ? pending : '✓';
        return {
          ...link,
          name: <>{link.name} <span className={`menu-badge ${badgeModifier}`}>{badgeText}</span></>,
        };
      }
      return link;
    });
    const canUseDarkMode = (this.props.currentUser?.Role?.permission_level || 0) >= this.state.darkModeMinLevel;

    return (
      <header className="site-header">
        <nav className="nav">
          <Link to="/" className="logo">
            <img src={logoSrc} alt="CLEAR" />
          </Link>

          <SiteNav links={filteredSiteLinks} />
        </nav>

        {!!this.props.currentUser &&
          <nav className="nav nav-right">
            <Menu
              currentUser={this.props.currentUser}
              endLoginAs={this.props.endLoginAs}
              icon={<UserAvatar user={this.props.currentUser?.User} size={30} />}
              links={filteredUserLinks}
              collapse={true}
              align="right"
              onThemeChange={canUseDarkMode ? this.props.onThemeChange : undefined}
            />
          </nav>
        }
      </header>
    );
  };
}

Header.propTypes = {
  currentUser: PropTypes.shape({
    user_id: PropTypes.number,
    Role: PropTypes.shape({
      permission_level: PropTypes.number
    })
  })
}

export default Header;